import React from "react";
import { ErrorState } from "./StateMessage";

// Recognizes error strings that shouldn't reach end users and swaps them
// for a friendly, action-oriented message. Applied at every panel-level
// error surface so raw Postgres/PostgREST text never leaks to the UI.
//
// Keep this list additive — anything unmatched falls through to a generic
// "Unavailable" so we still hide low-level noise even for unknown shapes.
// (Developers get the raw message on the console; end users don't.)
const RAW_PATTERNS = [
  { re: /permission denied for (table|schema|function|view|relation)/i, friendly: "This view is temporarily unavailable. Please try again shortly." },
  { re: /^42501/,                                                       friendly: "This view is temporarily unavailable. Please try again shortly." },
  { re: /relation ".*" does not exist/i,                                friendly: "This view is temporarily unavailable while backend updates ship." },
  { re: /^42P01/,                                                       friendly: "This view is temporarily unavailable while backend updates ship." },
  { re: /column ".*" does not exist/i,                                  friendly: "This view is temporarily unavailable while backend updates ship." },
  { re: /^42703/,                                                       friendly: "This view is temporarily unavailable while backend updates ship." },
  { re: /function .* does not exist/i,                                  friendly: "This view is temporarily unavailable while backend updates ship." },
  { re: /^PGRST202/,                                                    friendly: "This view is temporarily unavailable while backend updates ship." },
  { re: /statement timeout/i,                                           friendly: "This query is taking longer than expected. Try narrowing the date range." },
  { re: /^57014/,                                                       friendly: "This query is taking longer than expected. Try narrowing the date range." },
  { re: /Failed to fetch/i,                                             friendly: "Couldn't reach the server. Check your connection and retry." },
  { re: /NetworkError/i,                                                friendly: "Couldn't reach the server. Check your connection and retry." },
];

// Match anything that reads like a Postgres error and hasn't been claimed
// by a specific pattern above — sqlstate prefixes, "duplicate key value",
// "invalid input syntax", "violates ... constraint", etc.
const GENERIC_DB_HINT = /^(?:[0-9A-Z]{5}|duplicate key|invalid input syntax|violates .* constraint|constraint .* violation)/i;

export const sanitizePanelError = (raw) => {
  if (raw == null) return "";
  const msg = String(raw).trim();
  if (!msg) return "";
  for (const { re, friendly } of RAW_PATTERNS) {
    if (re.test(msg)) return friendly;
  }
  if (GENERIC_DB_HINT.test(msg)) {
    return "Something went wrong loading this view. Please try again.";
  }
  // Unknown shape — pass through, but only if it doesn't look database-y.
  // Non-DB errors (user-facing "No data" text, custom API messages) are
  // already appropriate for display.
  return msg;
};

// Class-based error boundary for render-phase crashes (undefined.map,
// missing prop, etc.). Swaps the raw crash for the same friendly message
// pipe so the UX is consistent whether the error came from a bad fetch
// or a bad render. `label` names the panel for debug logs.
export default class PanelErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error(`[PanelErrorBoundary${this.props.label ? ` · ${this.props.label}` : ""}]`, error, info);
  }

  render() {
    if (this.state.hasError) {
      const friendly = sanitizePanelError(this.state.error?.message || String(this.state.error));
      return <ErrorState variant="inset" message={friendly || "Something went wrong loading this view. Please try again."} />;
    }
    return this.props.children;
  }
}
